import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { ChevronLeft, ChevronRight, Edit3, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const FAMILIES = ["", "citrico", "floral", "amaderado", "oriental", "gourmand"];
const BULK_FAMILIES = FAMILIES.filter(Boolean);
const PAGE_SIZE = 20;
const PRICE_OPS = [
  { value: "set", label: "Fijar precio" },
  { value: "multiply", label: "Multiplicar (ej: 1.10 = +10%)" },
  { value: "add", label: "Sumar (ej: 5 = +$5)" },
];

export default function AdminList() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("");
  const [debQ, setDebQ] = useState("");
  const [busy, setBusy] = useState(true);
  const [selected, setSelected] = useState(new Set());

  // Bulk action state
  const [bulkFamily, setBulkFamily] = useState("");
  const [bulkOp, setBulkOp] = useState("multiply");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setDebQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => setPage(0), [debQ, family]);

  const fetchRows = async () => {
    setBusy(true);
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (debQ) params.q = debQ;
    if (family) params.family = family;
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/products", { params }),
        api.get("/admin/products/count", { params: { q: debQ || undefined, family: family || undefined } }),
      ]);
      setRows(r1.data);
      setTotal(r2.data.count);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, debQ, family]);

  if (loading || !user?.is_admin) {
    return <div className="p-24 text-center text-muted-foreground">Cargando…</div>;
  }

  const nPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someChecked = rows.some((r) => selected.has(r.id));

  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) rows.forEach((r) => next.delete(r.id));
    else rows.forEach((r) => next.add(r.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const clearSelection = () => setSelected(new Set());

  const applyBulk = async (mode) => {
    if (selected.size === 0) return;
    const payload = { ids: Array.from(selected) };
    if (mode === "family") {
      if (!bulkFamily) { toast.error("Selecciona una familia"); return; }
      payload.family = bulkFamily;
    }
    if (mode === "price") {
      const v = parseFloat(bulkValue);
      if (isNaN(v)) { toast.error("Introduce un valor numérico"); return; }
      payload.price_operation = bulkOp;
      payload.price_value = v;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk", payload);
      toast.success(`${data.updated} productos actualizados`);
      clearSelection();
      setBulkFamily("");
      setBulkValue("");
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en la operación masiva");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12" data-testid="admin-list-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-caps text-royal">Backoffice</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-2">Catálogo · {total.toLocaleString()} productos</h1>
          <div className="mt-3 flex items-center gap-3 font-mono-caps text-xs">
            <span className="text-navy">Productos</span>
            <Link to="/admin/pedidos" className="text-muted-foreground hover:text-navy editorial-link" data-testid="admin-nav-orders">Pedidos</Link>
            <Link to="/admin/analytics" className="text-muted-foreground hover:text-navy editorial-link" data-testid="admin-nav-analytics">Analytics</Link>
            <Link to="/admin/ajustes/ups" className="text-muted-foreground hover:text-navy editorial-link" data-testid="admin-nav-ups">Ajustes UPS</Link>
            <Link to="/admin/emails" className="text-muted-foreground hover:text-navy editorial-link" data-testid="admin-nav-emails">Emails</Link>
            <Link to="/admin/fraude" className="text-muted-foreground hover:text-navy editorial-link" data-testid="admin-nav-fraud">Antifraude</Link>
          </div>
        </div>
        <div className="flex gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, marca, #item"
            className="w-64 border border-border p-3 focus:border-navy outline-none"
            data-testid="admin-search"
          />
          <select
            value={family}
            onChange={(e) => setFamily(e.target.value)}
            className="border border-border p-3 focus:border-navy outline-none capitalize"
            data-testid="admin-family-filter"
          >
            {FAMILIES.map((f) => (
              <option key={f} value={f}>{f ? f : "Todas las familias"}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="sticky top-24 z-30 bg-navy text-cream p-4 mb-4 flex flex-wrap items-center gap-4" data-testid="bulk-bar">
          <div className="font-mono-caps text-xs">
            {selected.size} seleccionado{selected.size !== 1 && "s"}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={bulkFamily}
              onChange={(e) => setBulkFamily(e.target.value)}
              className="bg-navy border border-cream/30 text-cream px-3 py-2 outline-none capitalize"
              data-testid="bulk-family-select"
            >
              <option value="">Cambiar familia…</option>
              {BULK_FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <button
              onClick={() => applyBulk("family")}
              disabled={bulkBusy || !bulkFamily}
              className="border border-cream px-3 py-2 hover:bg-cream hover:text-navy transition-colors font-mono-caps text-xs disabled:opacity-40"
              data-testid="bulk-apply-family"
            >
              Aplicar
            </button>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={bulkOp}
              onChange={(e) => setBulkOp(e.target.value)}
              className="bg-navy border border-cream/30 text-cream px-3 py-2 outline-none"
              data-testid="bulk-price-op"
            >
              {PRICE_OPS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
            </select>
            <input
              type="number"
              step="0.01"
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              placeholder="valor"
              className="w-28 bg-navy border border-cream/30 text-cream px-3 py-2 outline-none"
              data-testid="bulk-price-value"
            />
            <button
              onClick={() => applyBulk("price")}
              disabled={bulkBusy || !bulkValue}
              className="border border-cream px-3 py-2 hover:bg-cream hover:text-navy transition-colors font-mono-caps text-xs disabled:opacity-40"
              data-testid="bulk-apply-price"
            >
              Aplicar
            </button>
          </div>

          <button
            onClick={clearSelection}
            className="ml-auto flex items-center gap-2 font-mono-caps text-xs text-cream/80 hover:text-cream"
            data-testid="bulk-clear"
          >
            <X strokeWidth={1.25} className="h-3 w-3" /> Deseleccionar
          </button>
        </div>
      )}

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-products-table">
          <thead className="bg-muted font-mono-caps text-xs">
            <tr>
              <th className="text-left px-3 py-3 w-10">
                <Checkbox
                  checked={allChecked}
                  onCheckedChange={toggleAll}
                  className="rounded-none"
                  data-testid="admin-select-all"
                />
              </th>
              <th className="text-left px-4 py-3 w-16">Img</th>
              <th className="text-left px-4 py-3">Nombre</th>
              <th className="text-left px-4 py-3">Marca</th>
              <th className="text-left px-4 py-3">Familia</th>
              <th className="text-left px-4 py-3">Conc.</th>
              <th className="text-right px-4 py-3">Precio</th>
              <th className="text-right px-4 py-3">Stock</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {busy && (<tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">Cargando…</td></tr>)}
            {!busy && rows.length === 0 && (<tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">Sin resultados</td></tr>)}
            {!busy && rows.map((p) => (
              <tr key={p.id} className={`border-t border-border ${selected.has(p.id) ? "bg-navy/5" : "hover:bg-muted/40"}`} data-testid={`admin-row-${p.id}`}>
                <td className="px-3 py-2">
                  <Checkbox
                    checked={selected.has(p.id)}
                    onCheckedChange={() => toggleOne(p.id)}
                    className="rounded-none"
                    data-testid={`admin-checkbox-${p.id}`}
                  />
                </td>
                <td className="px-4 py-2">
                  <img src={p.images[0]} alt="" className="w-12 h-12 object-contain bg-white border border-border" />
                </td>
                <td className="px-4 py-2 font-serif">{p.name}</td>
                <td className="px-4 py-2 font-mono-caps text-xs">{p.brand}</td>
                <td className="px-4 py-2 capitalize">{p.family}</td>
                <td className="px-4 py-2">{p.concentration}</td>
                <td className="px-4 py-2 text-right">${p.sizes[0]?.price.toFixed(2)}</td>
                <td className="px-4 py-2 text-right">{p.sizes[0]?.stock ?? 0}</td>
                <td className="px-4 py-2 text-right">
                  <Link to={`/admin/producto/${p.id}`} className="inline-flex items-center gap-1 font-mono-caps text-xs border border-navy px-3 py-1 hover:bg-navy hover:text-cream transition-colors" data-testid={`admin-edit-${p.id}`}>
                    <Edit3 strokeWidth={1.25} className="h-3 w-3" /> Editar
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-6 font-mono-caps text-xs" data-testid="admin-pagination">
        <div>Página {page + 1} de {nPages}</div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="border border-border px-3 py-2 hover:border-navy disabled:opacity-30"
            data-testid="admin-prev-page"
          >
            <ChevronLeft strokeWidth={1.25} className="h-4 w-4" />
          </button>
          <button
            onClick={() => setPage((p) => Math.min(nPages - 1, p + 1))}
            disabled={page >= nPages - 1}
            className="border border-border px-3 py-2 hover:border-navy disabled:opacity-30"
            data-testid="admin-next-page"
          >
            <ChevronRight strokeWidth={1.25} className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
